import { useEffect, useRef, useState } from 'react'
import { ApiError, api } from '../api/client'
import type {
  PaywallAction,
  PaywallDimensionCheck,
  PaywallMatrix,
  PaywallReport,
  PaywallVariant,
} from '../api/types'
import { useAsync } from '../api/useReport'
import { Banner } from '../components/Banner'
import {
  SkeletonChart,
  SkeletonPage,
  SkeletonTable,
  SkeletonTiles,
} from '../components/Skeleton'
import { TrendChart } from '../components/TrendChart'
import { WindowPicker } from '../components/WindowPicker'
import { formatChange, formatDate, formatRange } from '../format'

interface Props {
  end: string
  days: number
  maxDate: string
  propertyId?: string
  onWindowChange: (next: { end?: string; days?: number }) => void
}

function tone(percent: number): 'up' | 'down' | 'flat' {
  if (percent > 0) return 'up'
  if (percent < 0) return 'down'
  return 'flat'
}

function count(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function rate(value: number): string {
  return `${value.toFixed(1)}%`
}

/**
 * Render a naive GA4 hour without constructing a `Date`.
 *
 * These stamps are in the *property's* reporting timezone, not the viewer's. Parsing
 * one would have the browser reinterpret it as local time and shift it — which is
 * exactly the mistake the backend refuses to make when it measures against GA4's own
 * frontier instead of the clock.
 */
function formatHour(stamp: string | null): string {
  if (!stamp) return '—'
  const [day, time] = stamp.split('T')
  if (!day || !time) return stamp
  return `${formatDate(day)} ${time.slice(0, 5)}`
}

/**
 * The paywall: how many sessions saw one, what they did about it, and which variant.
 *
 * Window-shaped rather than day-shaped, unlike the funnel view. `analyse_paywall` sums
 * every figure over the window and divides each rate out of those sums, which is an
 * aggregation the query genuinely performs — so a range selector here promises only
 * what the backend delivers.
 *
 * Nothing on this page computes. Every count, rate and share arrives from the backend,
 * for the same reason the evidence package does: a number a reader sees here and a
 * number the agent narrates must be the same number.
 */
export function Paywall({ end, days, maxDate, propertyId, onWindowChange }: Props) {
  const { data, error, loading } = useAsync<PaywallReport>(
    (signal) => api.paywall({ end, days, propertyId }, signal),
    [end, days, propertyId],
    true,
    `paywall:${propertyId ?? ''}:${end}:${days}`,
  )

  // Offered whenever the split has nothing to say, which is exactly when a reader needs
  // it: a freshly registered custom dimension and a broken one are indistinguishable in
  // a settled report, because neither has a value on any settled day.
  const splitIsEmpty =
    data !== null &&
    (data.variants.length === 0 ||
      (data.variants.length === 1 && data.variants[0].paywall_id === '(not set)'))

  return (
    <>
      <WindowPicker end={end} days={days} maxDate={maxDate} onChange={onWindowChange} disabled={loading} />

      {splitIsEmpty && <DimensionCheck propertyId={propertyId} />}

      {error && <Banner tone="critical">{error}</Banner>}
      {loading && (
        <SkeletonPage label="Loading the paywall…">
          <SkeletonTiles count={4} />
          <SkeletonChart />
          <SkeletonTable rows={5} columns={6} />
        </SkeletonPage>
      )}

      {data && !loading && (
        <>
          <p className="section__note" style={{ marginTop: 0 }}>
            {formatRange(data.window.current_start, data.window.current_end)} · compared with{' '}
            {formatRange(data.window.comparison_start, data.window.comparison_end)}
          </p>

          <Totals total={data.total} />

          {data.status === 'ok' && (
            <section className="section">
              {data.series.map((series) => (
                <TrendChart key={series.metric} series={series} comparisonLabel="Previous period" />
              ))}
            </section>
          )}

          <Variants report={data} />

          {data.notes.map((note) => (
            <p className="breakdown__note" key={note}>
              {note}
            </p>
          ))}
        </>
      )}
    </>
  )
}

/**
 * "Is paywall_id arriving yet?" — asked on demand, never on load.
 *
 * A live GA4 call that spends quota to answer a question about configuration, so it is
 * behind a button for the same reason `check_access` is. What it reads are the hours
 * GA4 has already processed, which is the only place the answer exists: a custom
 * dimension carries values from the moment it is registered and is never backfilled, so
 * for the first couple of days every settled day says `(not set)` regardless of whether
 * the registration was done right.
 *
 * Every claim it shows is one-sided. A value that has appeared is a fact; a value that
 * has not is a suspicion, and the wording never upgrades one into the other.
 */
function DimensionCheck({ propertyId }: { propertyId?: string }) {
  const [result, setResult] = useState<PaywallDimensionCheck | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function run() {
    setBusy(true)
    setFailed(null)
    try {
      setResult(await api.paywallCheck({ propertyId }))
    } catch (cause) {
      setFailed(cause instanceof ApiError ? cause.message : 'Could not reach the server.')
    } finally {
      setBusy(false)
    }
  }

  // Only `not_set_only` is a warning: it is the one state with something to fix. The
  // other three are informational, including "carrying" — nothing is wrong, the settled
  // report simply has not caught up yet.
  const tones: Record<PaywallDimensionCheck['state'], 'warning' | 'info'> = {
    carrying: 'info',
    not_set_only: 'warning',
    no_impressions: 'info',
    no_data: 'info',
  }

  return (
    <div className="card">
      <div className="section__head">
        <h2 className="section__title">No paywall split yet</h2>
        <button type="button" className="button secondary" disabled={busy} onClick={run}>
          {busy ? 'Checking…' : 'Check GA4 now'}
        </button>
      </div>

      <p className="section__note">
        A custom dimension carries values only from the moment it is registered, and GA4
        never backfills one — so every settled day before then reads <code>(not set)</code>{' '}
        whether the setup is right or wrong. This checks the hours GA4 has already
        processed, which is the only place the difference shows.
      </p>

      {failed && <Banner tone="critical">{failed}</Banner>}

      {result && (
        <>
          <Banner tone={tones[result.state]}>{result.detail}</Banner>

          {result.values.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Paywall</th>
                    <th scope="col">Events seen</th>
                    <th scope="col">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {result.values.map((value) => (
                    <tr key={value.paywall_id}>
                      <th scope="row">{value.paywall_id}</th>
                      <td>{count(value.events)}</td>
                      <td>{formatHour(value.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="breakdown__note">
            Counted over the last {result.days_examined} days of processed hours, in the
            property’s own reporting timezone.
            {result.not_set_events > 0 &&
              ` ${count(result.not_set_events)} impressions carried no paywall_id — expected for anything collected before you registered it.`}
          </p>
        </>
      )}
    </div>
  )
}

/**
 * Headline impressions and the two actions taken on them.
 *
 * Each action shows its rate as the value and its session count underneath, rather
 * than the other way round: the count moves whenever traffic moves, and the rate is
 * the thing that says whether the paywall itself changed.
 */
function Totals({ total }: { total: PaywallVariant }) {
  return (
    <div className="tiles" style={{ marginTop: 14 }}>
      <div className="tile tile--hero">
        <span className="tile__label">Paywall views</span>
        <span className="tile__value">{count(total.views)}</span>
        <span className={`tile__delta tile__delta--${tone(total.views_change_percent)}`}>
          {formatChange(total.views_change_percent)}
          <span className="tile__delta-note">vs previous period</span>
        </span>
      </div>

      {total.actions.map((action) => (
        <div className="tile tile--hero" key={action.event_name}>
          <span className="tile__label">{action.label}</span>
          <span className="tile__value" title={`${count(action.sessions)} sessions`}>
            {rate(action.rate_percent)}
          </span>
          <span className={`tile__delta tile__delta--${tone(action.rate_change_percent)}`}>
            {formatChange(action.rate_change_percent)}
            <span className="tile__delta-note">
              {count(action.sessions)} sessions · rate vs previous period
            </span>
          </span>
        </div>
      ))}

      {/* Items sold, not sessions — the one tile here counting a different thing, which
          is why it shows a count where its neighbours show a rate. */}
      <div className="tile tile--hero">
        <span className="tile__label">Purchases</span>
        <span className="tile__value">{count(total.purchases)}</span>
        <span className={`tile__delta tile__delta--${tone(total.purchases_change_percent)}`}>
          {formatChange(total.purchases_change_percent)}
          <span className="tile__delta-note">items sold · vs previous period</span>
        </span>
      </div>
    </div>
  )
}

/**
 * The same three events, per paywall id.
 *
 * The rows do not sum to the totals above and the backend says so in a note: GA4
 * de-duplicates sessions per dimension value, so a session shown two paywalls is
 * counted in both.
 */
function Variants({ report }: { report: PaywallReport }) {
  const actions: PaywallAction[] = report.variants[0]?.actions ?? report.total.actions
  const [byDate, setByDate] = useState(false)

  // An empty table and a suppressed one look identical to a reader and mean opposite
  // things, so a split with nothing in it says which of the two happened.
  if (report.variants.length === 0) {
    return (
      <div className="card">
        <p className="placeholder">
          {report.suppressed_variants > 0
            ? `Nothing shown: every paywall id was below the ${report.minimum_segment_users}-user
               reporting floor. The totals above are unaffected.`
            : 'No paywall_id values were returned for this window.'}
        </p>
      </div>
    )
  }

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">By paywall</h2>
        <span className="section__note">
          {report.variants.length} shown, largest first
          {report.matrix && (
            <>
              {' · '}
              <button
                type="button"
                className="button secondary"
                onClick={() => setByDate((on) => !on)}
              >
                {byDate ? 'Show window totals' : 'Split by date'}
              </button>
            </>
          )}
        </span>
      </div>

      {byDate && report.matrix && (
        <Matrix matrix={report.matrix} actions={actions} />
      )}

      {!byDate && (

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Paywall</th>
                <th scope="col">Share</th>
                <th scope="col">Views</th>
                {actions.map((action) => (
                  <th scope="col" key={action.event_name}>
                    {action.label}
                  </th>
                ))}
                <th scope="col">Purchases</th>
              </tr>
            </thead>
            <tbody>
              {report.variants.map((variant) => (
                <tr key={variant.paywall_id}>
                  <th scope="row">{variant.paywall_id}</th>
                  <td>{rate(variant.share_of_views_percent)}</td>
                  <td>
                    {count(variant.views)}
                    <span className={`funnel__cell-rate tile__delta--${tone(variant.views_change_percent)}`}>
                      {' '}
                      {formatChange(variant.views_change_percent)}
                    </span>
                  </td>
                  {variant.actions.map((action) => (
                    <td key={action.event_name}>
                      {rate(action.rate_percent)}
                      <span className="funnel__cell-rate"> {count(action.sessions)}</span>
                    </td>
                  ))}
                  {/* A count, not a rate: purchases are items sold, the columns beside
                      them are sessions containing a click, and dividing one by the other
                      would read as a conversion rate between two different units. */}
                  <td>
                    {count(variant.purchases)}
                    <span
                      className={`funnel__cell-rate tile__delta--${tone(variant.purchases_change_percent)}`}
                    >
                      {' '}
                      {formatChange(variant.purchases_change_percent)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* From `report.total`, which the backend computes with no breakdown
                dimension — never by adding this column up. It will not match the column,
                and the difference runs both ways: de-duplication pushes the sum above
                the truth, withheld and unshown ids pull it below. The note says so. */}
            <tfoot>
              <tr className="table__total">
                <th scope="row">{report.total.paywall_id}</th>
                <td title="A share of the total out of the total is not a reading.">—</td>
                <td>
                  {count(report.total.views)}
                  <span
                    className={`funnel__cell-rate tile__delta--${tone(report.total.views_change_percent)}`}
                  >
                    {' '}
                    {formatChange(report.total.views_change_percent)}
                  </span>
                </td>
                {report.total.actions.map((action) => (
                  <td key={action.event_name}>
                    {rate(action.rate_percent)}
                    <span className="funnel__cell-rate"> {count(action.sessions)}</span>
                  </td>
                ))}
                <td>
                  {count(report.total.purchases)}
                  <span
                    className={`funnel__cell-rate tile__delta--${tone(report.total.purchases_change_percent)}`}
                  >
                    {' '}
                    {formatChange(report.total.purchases_change_percent)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="breakdown__note">
          The total is measured, not added up: it comes from the query with no paywall
          split, the same source as the tiles at the top of the page. It will not match
          the column above, and can sit either side of it — a session that saw two
          paywalls is counted once under each id, which pushes the column up, while
          impressions carrying an id that was withheld or not listed are missing from the
          column entirely, which pushes it down. Share is left blank for the same reason:
          the shares are taken out of the split's impressions, a different number again.
        </p>
      </div>
      )}
    </section>
  )
}

/** Weekday headings, Monday first — the week the reporting calendar is read in. */
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** A chosen span of days, inclusive at both ends. A single day has start === end. */
interface DaySpan {
  start: string
  end: string
}

/**
 * The days of one month, laid out as weeks, from a set of ISO day strings.
 *
 * Split and rebuilt by hand rather than parsed. These stamps are days in the *property's*
 * reporting timezone; handing one to `new Date('2026-08-28')` gives a UTC midnight the
 * browser then renders in the viewer's zone, which in the Americas shows the day before.
 * `Date.UTC` is used only to ask which weekday a date falls on — a question about the
 * calendar, not about a moment — and never to produce a label.
 */
function monthGrid(year: number, month: number): (string | null)[][] {
  const first = new Date(Date.UTC(year, month - 1, 1))
  const length = new Date(Date.UTC(year, month, 0)).getUTCDate()
  // getUTCDay is Sunday-first; shift it so Monday is column zero.
  const lead = (first.getUTCDay() + 6) % 7

  const cells: (string | null)[] = Array(lead).fill(null)
  for (let day = 1; day <= length; day += 1) {
    cells.push(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`)
  }
  while (cells.length % 7 !== 0) cells.push(null)

  const weeks: (string | null)[][] = []
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7))
  }
  return weeks
}

/** Order-free span between two days: whichever was clicked first, the span reads forward. */
function span(a: string, b: string): DaySpan {
  return a <= b ? { start: a, end: b } : { start: b, end: a }
}

/**
 * Pick a day, or a span of days, out of the window.
 *
 * Two clicks, the way GA4's own picker works: the first sets one end and selects that day
 * alone, the second closes the span and everything between the two comes with it. Clicking
 * the same day twice therefore means that day and nothing else — the single-day case falls
 * out of the same gesture rather than needing one of its own.
 *
 * Between the two clicks the span under the cursor is shown, because a range you cannot
 * see until you commit to it is a range you get wrong and have to redo.
 *
 * Only days the report covers are selectable. A day outside the window is drawn but dead:
 * showing the month with holes in it says "the window ends here" far more plainly than a
 * calendar that accepts the click and then changes nothing.
 */
function DayPicker({
  available,
  chosen,
  onChoose,
  onClear,
}: {
  available: readonly string[]
  chosen: DaySpan | null
  onChoose: (next: DaySpan) => void
  onClear: () => void
}) {
  const usable = new Set(available)
  // The half-finished click. Null whenever the last gesture completed, which is what makes
  // the next click start a new span rather than extend the old one.
  const [anchor, setAnchor] = useState<string | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)

  // Every month the window touches, oldest first, so the calendar reads downwards the way
  // a calendar does — even though the table beneath it is newest first.
  const months = [...new Set(available.map((date) => date.slice(0, 7)))].sort()

  // What the calendar paints: the committed span, or the one being drawn under the cursor.
  const preview = anchor ? span(anchor, hovered ?? anchor) : chosen

  function click(date: string) {
    if (anchor === null) {
      setAnchor(date)
      onChoose({ start: date, end: date })
      return
    }
    setAnchor(null)
    onChoose(span(anchor, date))
  }

  return (
    <div className="daypicker" onMouseLeave={() => setHovered(null)}>
      <div className="daypicker__months">
        {months.map((stamp) => {
          const [year, month] = stamp.split('-').map(Number)
          return (
            <div className="daypicker__month" key={stamp}>
              <div className="daypicker__title">
                {MONTH_NAMES[month - 1]} {year}
              </div>
              <table className="daypicker__grid">
                <thead>
                  <tr>
                    {WEEKDAYS.map((letter, index) => (
                      <th scope="col" key={`${letter}${index}`} abbr={letter}>
                        {letter}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthGrid(year, month).map((week) => (
                    <tr key={week.find((date) => date !== null) ?? `${stamp}:${week.length}`}>
                      {week.map((date, index) => {
                        const inside =
                          date !== null &&
                          preview !== null &&
                          date >= preview.start &&
                          date <= preview.end
                        const edge =
                          inside && (date === preview.start || date === preview.end)
                        return (
                          <td key={date ?? `pad:${index}`}>
                            {date && (
                              <button
                                type="button"
                                className={`daypicker__day${inside ? ' is-inside' : ''}${
                                  edge ? ' is-edge' : ''
                                }`}
                                disabled={!usable.has(date)}
                                aria-pressed={inside}
                                title={
                                  usable.has(date) ? undefined : 'Outside the reported window'
                                }
                                onMouseEnter={() => setHovered(date)}
                                onFocus={() => setHovered(date)}
                                onClick={() => click(date)}
                              >
                                {Number(date.slice(8))}
                              </button>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        })}
      </div>

      <div className="daypicker__foot">
        <button
          type="button"
          className="button secondary"
          disabled={chosen === null}
          onClick={() => {
            setAnchor(null)
            onClear()
          }}
        >
          All dates
        </button>
        <span className="section__note">
          {anchor
            ? 'Pick the other end, or click the same day again for one day'
            : chosen === null
              ? 'Every day in the window'
              : chosen.start === chosen.end
                ? formatDate(chosen.start)
                : `${formatDate(chosen.start)} – ${formatDate(chosen.end)}`}
        </span>
      </div>
    </div>
  )
}

/**
 * The split cut by day: a date heading, then that day's paywalls beneath it.
 *
 * Grouped this way rather than as a date axis because the reading it supports is "on
 * this day, how did the paywalls compare" — so each day carries the same columns as the
 * window table above, ranked largest first within the day. The ranking moves between
 * days, which is the whole reason to cut the window open.
 *
 * The calendar narrows what is *shown*, never what was measured: every figure was
 * computed over the window the picker at the top of the page chose, and hiding a day here
 * does not recompute a share or a rate. The note says so, because a filtered table
 * otherwise reads as a smaller report.
 *
 * Nothing here computes. Every rate and share arrives from the backend, and a suppressed
 * row is drawn as a dash rather than as the zeros its payload carries — "too few people"
 * and "nobody" mean opposite things and must not render the same.
 */
function Matrix({ matrix, actions }: { matrix: PaywallMatrix; actions: PaywallAction[] }) {
  // Read off the data rather than hard-coded, so the header and the body cannot disagree
  // about which checkout steps exist: a step the backend stops sending stops being a
  // column, instead of becoming a column of blanks.
  const steps =
    matrix.days
      .flatMap((day) => day.rows)
      .find((row) => row.cell.journey.length > 0)
      ?.cell.journey.map((step) => ({ eventName: step.event_name, label: step.label })) ?? []

  const [picking, setPicking] = useState(false)
  const [chosen, setChosen] = useState<DaySpan | null>(null)
  // Collapsed rather than expanded, so the default is the whole window open and an empty
  // set means nothing is hidden.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  const anchored = useRef<HTMLDivElement | null>(null)

  // A popover overlays what it covers rather than pushing it down, so it has to be closed
  // by the two gestures a reader will already try: a click elsewhere, and Escape.
  useEffect(() => {
    if (!picking) return
    function away(event: MouseEvent) {
      if (!anchored.current?.contains(event.target as Node)) setPicking(false)
    }
    function key(event: KeyboardEvent) {
      if (event.key === 'Escape') setPicking(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', key)
    }
  }, [picking])

  // No span means every day: the untouched state is the useful one.
  const shown = chosen
    ? matrix.days.filter((day) => day.date >= chosen.start && day.date <= chosen.end)
    : matrix.days
  const filtered = chosen !== null
  const allCollapsed = shown.length > 0 && shown.every((day) => collapsed.has(day.date))

  function toggleDay(date: string) {
    setCollapsed((current) => {
      const next = new Set(current)
      if (!next.delete(date)) next.add(date)
      return next
    })
  }

  const label = !chosen
    ? 'All dates'
    : chosen.start === chosen.end
      ? formatDate(chosen.start)
      : `${formatDate(chosen.start)} – ${formatDate(chosen.end)}`

  return (
    <div className="card">
      <div className="controls matrix__controls">
        <div className="matrix__anchor" ref={anchored}>
          <button
            type="button"
            className="button secondary"
            aria-expanded={picking}
            onClick={() => setPicking((open) => !open)}
          >
            {label}
          </button>

          {picking && (
            <div className="daypicker__pop">
              <DayPicker
                available={matrix.dates}
                chosen={chosen}
                onChoose={setChosen}
                onClear={() => setChosen(null)}
              />
            </div>
          )}
        </div>

        <button
          type="button"
          className="button secondary"
          onClick={() =>
            setCollapsed(allCollapsed ? new Set() : new Set(shown.map((day) => day.date)))
          }
        >
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </button>
      </div>

      {matrix.journey_unattributed_only && (
        <p className="verdict verdict--warning">
          <strong>The checkout columns are unavailable for this window, not zero.</strong>{' '}
          Journeys were recorded, but not one of them named a paywall: the{' '}
          <code>entry_point</code> parameter only began naming individual paywalls on 26
          August 2026, and GA4 never backfills a custom dimension. The impression and
          click columns beside them are unaffected.
        </p>
      )}

      {shown.length === 0 ? (
        <p className="placeholder">No day in the window falls in that range.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Paywall</th>
                <th scope="col">Share</th>
                <th scope="col">Views</th>
                {actions.map((action) => (
                  <th scope="col" key={action.event_name}>
                    {action.label}
                  </th>
                ))}
                {steps.map((step) => (
                  <th scope="col" key={step.eventName}>
                    {step.label}
                  </th>
                ))}
                <th scope="col">Purchases</th>
              </tr>
            </thead>
            {shown.map((day) => {
              const folded = collapsed.has(day.date)
              return (
                <tbody key={day.date}>
                  <tr className="matrix__day">
                    <th scope="colgroup" colSpan={3 + actions.length + steps.length + 1}>
                      <button
                        type="button"
                        className="matrix__toggle"
                        aria-expanded={!folded}
                        onClick={() => toggleDay(day.date)}
                      >
                        <span aria-hidden="true">{folded ? '▸' : '▾'}</span>
                        {formatDate(day.date)}
                        <span className="funnel__cell-rate">
                          {' '}
                          {count(day.split_views)} split views · {day.rows.length} paywall
                          {day.rows.length === 1 ? '' : 's'}
                          {day.unattributed_purchases > 0 &&
                            ` · ${count(day.unattributed_purchases)} sale${day.unattributed_purchases === 1 ? '' : 's'} from no paywall`}
                        </span>
                      </button>
                    </th>
                  </tr>
                  {!folded &&
                    day.rows.map((row) => (
                      <tr key={`${day.date}:${row.paywall_id}`}>
                        <th scope="row" className="matrix__nested">
                          {row.paywall_id}
                        </th>
                        {row.cell.suppressed ? (
                          <td colSpan={2 + actions.length + steps.length + 1} title="Below the reporting floor">
                            — below the {matrix.minimum_segment_users}-user floor on this day
                          </td>
                        ) : (
                          <>
                            <td>{rate(row.cell.share_of_views_percent)}</td>
                            <td>{count(row.cell.views)}</td>
                            {row.cell.actions.map((action) => (
                              <td key={action.event_name}>
                                {rate(action.rate_percent)}
                                <span className="funnel__cell-rate"> {count(action.sessions)}</span>
                              </td>
                            ))}
                            {steps.map((step) => {
                              const reached = row.cell.journey.find(
                                (item) => item.event_name === step.eventName,
                              )
                              return (
                                <td key={step.eventName} className="matrix__journey">
                                  {reached ? rate(reached.rate_percent) : '—'}
                                  {reached && (
                                    <span className="funnel__cell-rate">
                                      {' '}
                                      {count(reached.sessions)}
                                    </span>
                                  )}
                                </td>
                              )
                            })}
                            <td>{count(row.cell.purchases)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  {/* Last row of the day, and measured rather than added: `day.total`
                      comes from the dimensionless query. It sits inside the fold with
                      the rows it totals, because a total visible while its rows are
                      hidden invites reading it as the sum it is not. */}
                  {!folded && day.total && (
                    <tr className="table__total">
                      <th scope="row" className="matrix__nested">
                        All paywalls
                      </th>
                      <td title="A share of the total out of the total is not a reading.">—</td>
                      <td>{count(day.total.views)}</td>
                      {day.total.actions.map((action) => (
                        <td key={action.event_name}>
                          {rate(action.rate_percent)}
                          <span className="funnel__cell-rate"> {count(action.sessions)}</span>
                        </td>
                      ))}
                      {/* Wider than the rows above: every session that reached the step,
                          whichever entry point it came from, because GA4 will not answer
                          "from any paywall". A count with no rate — the sessions are
                          site-wide and the only denominator to hand is this property's
                          paywall impressions, which is a different population. */}
                      {steps.map((step) => {
                        const reached = day.total?.journey.find(
                          (item) => item.event_name === step.eventName,
                        )
                        return (
                          <td
                            key={step.eventName}
                            className="matrix__journey"
                            title="Every session that reached this step, including journeys that began away from a paywall."
                          >
                            {reached ? count(reached.sessions) : '—'}
                          </td>
                        )
                      })}
                      <td
                        title={
                          day.withheld_purchases > 0
                            ? `Included in this total: ${count(day.withheld_purchases)} sale(s) on rows withheld by the ${matrix.minimum_segment_users}-user floor, or carrying no paywall id at all.`
                            : undefined
                        }
                      >
                        {count(day.total.purchases)}
                        {/* "incl.", never "+": the withheld sales are part of this total,
                            not an addition to it. Written as +1 first time out, which
                            read as 9 against a total of 8. */}
                        {day.withheld_purchases > 0 && (
                          <span className="funnel__cell-rate">
                            {' '}
                            incl. {count(day.withheld_purchases)} hidden
                          </span>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              )
            })}
          </table>
        </div>
      )}

      <p className="breakdown__note">
        Days are whole days in the property's reporting timezone, newest first, and each
        one is ranked on its own. The rows do not add up to the day's total, and the total
        is not made from them: GA4 de-duplicates sessions per paywall id and per day, so a
        session shown two paywalls counts in both rows and one spanning midnight counts in
        both days. Each day's total row is measured the way the tiles at the top of the
        page are — from the query with no paywall split — so it sits either side of its
        own rows: below them on a day when people saw more than one paywall, above them
        on a day when some impressions carried an id that was withheld or not listed.
        Share is out of that day's split impressions, which is why the total claims none.
        {steps.length > 0 &&
          ` Checkout steps carry no paywall_id, so they are attached by the entry_point parameter the journey carries — the same paywall-<id> string the sold items are tagged with. Their rates are out of this paywall's impressions. The total row adds up the journeys whose entry point named a paywall — including ids withheld from the table above — rather than asking GA4 for the step outright, which would sweep in the header and the newsletter too.`}
        {matrix.unattributed_journey_sessions > 0 &&
          ` ${count(matrix.unattributed_journey_sessions)} checkout sessions began somewhere that is not a paywall — the header, a newsletter — and are in no row here.`}
        {filtered &&
          ` Showing ${shown.length} of ${matrix.days.length} days — the figures are unchanged, because the calendar hides rows rather than narrowing the window the report was computed over.`}
        {matrix.days.some((day) => day.withheld_purchases > 0) &&
          ` Purchases are the one column that does add up — an item sits in exactly one category, so there is nothing for GA4 to de-duplicate. Where a day's total exceeds its rows, the difference is marked "incl. N hidden" — already inside the total, not added to it — and is a sale on a paywall the floor withheld. Sales belonging to no paywall at all are named on the day heading instead, so a suppression about impressions cannot take one with it.`}
        {matrix.suppressed_cells > 0 &&
          ` ${matrix.suppressed_cells} paywall-day${matrix.suppressed_cells === 1 ? '' : 's'} sat below the ${matrix.minimum_segment_users}-user floor and ${matrix.suppressed_cells === 1 ? 'is' : 'are'} withheld — too few people, not nobody.`}
      </p>
    </div>
  )
}
